import { useQuery } from "@tanstack/react-query";
import { flowsApi } from "../services/rag-service";

export const FLOW_RUNS_QUERY_KEY = ["flowRuns"];

// Matches AP's own real Runs page (flow-run-hooks.ts's refetchInterval) -
// a run already in progress when the tab loads has no other way to appear,
// since a plain query is a one-shot fetch, not a subscription.
const REFETCH_INTERVAL_MS = 7000;

export const useFlowRuns = ({
  projectIds,
  status,
  cursor,
  limit,
  enabled = true,
}: UseFlowRunsParams) => {
  // One stable string so placeholderData below compares by value, not array
  // identity - mirrors useMCPConnections.ts's own projectScope convention.
  const scope = `${projectIds.join(",") || "none"}:${status}`;

  const query = useQuery({
    queryKey: [...FLOW_RUNS_QUERY_KEY, scope, cursor, limit],
    queryFn: () =>
      flowsApi.getFlowRuns({
        projectIds,
        projectId: undefined,
        flowId: undefined,
        // The dropdown's values are lowercase for display; the API takes the
        // real uppercase run statuses.
        status: status === "all" ? [] : [status.toUpperCase()],
        cursor,
        limit,
      }),
    enabled: enabled && projectIds.length > 0,
    // Keep the previous page's rows while paginating within the SAME
    // project/status selection (so a page turn doesn't flash a skeleton),
    // but not across a selection change - showing the old selection's runs
    // under a new filter would read as if they belonged to it, and there
    // would be no loading indicator to signal otherwise (isLoading is false
    // whenever placeholderData supplies data).
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey?.[1] === scope ? previous : undefined,
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
};

interface UseFlowRunsParams {
  projectIds: string[];
  status: string;
  cursor: string;
  limit: number;
  enabled?: boolean;
}
